import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'

import { useAuth } from '@app/context/auth'
import {
  GetUserAcceptedCourseInvitesCountWithSplashScreensQuery,
  GetUserAcceptedCourseInvitesCountWithSplashScreensQueryVariables,
  InsertSubmissionOfSplashScreenMutation,
  InsertSubmissionOfSplashScreenMutationVariables,
  Splash_Screens_Enum,
} from '@app/generated/graphql'
import { INSERT_SUBMISSION_OF_SPLASH_SCREEN } from '@app/queries/submission_of_splash_screens/insert-submission-of-splash-screen'
import { RoleName } from '@app/types'
import { GET_USER_ACCEPTED_COURSE_INVITES_WITH_SPLASH_SCREENS } from '@app/queries/course-invites/get-user-course-invites'

const INVITE_ACCEPTED_VALUE = 'invite_accepted'

export const useProfileKnowledgeHubCourseInviteSplashScreen = () => {
  const { activeRole, profile } = useAuth()
  const location = useLocation()

  const isInviteJustAccepted =
    new URLSearchParams(location.search).get('success') ===
    INVITE_ACCEPTED_VALUE

  const isUser = Boolean(profile?.id && activeRole === RoleName.USER)
  const [{ data, fetching }] = useQuery<
    GetUserAcceptedCourseInvitesCountWithSplashScreensQuery,
    GetUserAcceptedCourseInvitesCountWithSplashScreensQueryVariables
  >({
    query: GET_USER_ACCEPTED_COURSE_INVITES_WITH_SPLASH_SCREENS,
    variables: { profileId: profile?.id ?? '' },
    pause: !isUser || !isInviteJustAccepted,
  })

  const acceptedCourseInvitesCount =
    data?.course_invites_aggregate.aggregate?.count ?? 0
  const seenCount = data?.course_invite_splash_screens?.[0]?.seen_count ?? 0

  // Unlike the other splash screens, the Knowledge Hub pop-up is not gated behind a PostHog feature flag.
  // If the user has accepted more course invites than they have acknowledged, the pop-up is enabled.
  // If they have acknowledged all accepted course invites, the pop-up is disabled.
  const isProfileSplashScreenPassed = acceptedCourseInvitesCount <= seenCount

  // Eligibility to ever see the pop-up: a USER who has not seen it yet. The actual
  // trigger (having just accepted a course invite) is handled by the component via
  // the redirect it lands on; "show once" (AC5/AC6) is backed by
  // submission_of_splash_screens.
  const isKnowledgeHubCourseInviteSplashScreenEnabled = Boolean(
    !isProfileSplashScreenPassed && profile?.id && activeRole === RoleName.USER,
  )

  const [, insertSubmissionOfSplashScreen] = useMutation<
    InsertSubmissionOfSplashScreenMutation,
    InsertSubmissionOfSplashScreenMutationVariables
  >(INSERT_SUBMISSION_OF_SPLASH_SCREEN)

  const insertSubmissionOfKnowledgeHubCourseInviteSplashScreen =
    useCallback(async () => {
      if (!profile?.id) return

      return insertSubmissionOfSplashScreen({
        profileId: profile.id,
        splashScreen: Splash_Screens_Enum.KnowledgeHubCourseInvite,
        seenCount: acceptedCourseInvitesCount,
      })
    }, [
      insertSubmissionOfSplashScreen,
      profile?.id,
      acceptedCourseInvitesCount,
    ])

  return {
    isKnowledgeHubCourseInviteSplashScreenEnabled,
    isKnowledgeHubCourseInviteSplashScreenLoading: fetching,
    insertSubmissionOfKnowledgeHubCourseInviteSplashScreen,
  }
}
